import { Component, ViewChild, inject } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, filter, map } from 'rxjs';
import { DrawerModule, Drawer } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { AvatarModule } from 'primeng/avatar';
import { StyleClassModule } from 'primeng/styleclass';
import { Popover } from 'primeng/popover';
import { AuthService } from '../../service/auth.service';
import { UserContextService } from '../../service/context/user-context.service';
import { PermissionContextService } from '../../service/context/permission-context.service';
import { ChangePasswordDialogComponent } from '../../shared/change-password-dialog/change-password-dialog.component';

interface SidebarItem {
  label: string;
  icon: string;
  link: string;
  permission?: string[];
  requiresHeadquarter?: boolean;
}

interface SidebarSection {
  label: string;
  icon: string;
  children: SidebarItem[];
}

interface SidebarData {
  mainItems: SidebarItem[];
  settingsItems: SidebarItem[];
  sdrfItems: SidebarSection[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    DrawerModule,
    ButtonModule,
    RippleModule,
    AvatarModule,
    StyleClassModule,
    Popover,
    ChangePasswordDialogComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  @ViewChild('drawerRef') drawerRef!: Drawer;
  @ViewChild('changePasswordDialog')
  changePasswordDialog!: ChangePasswordDialogComponent;

  private sidebarData = new BehaviorSubject<SidebarData | null>(null);
  sidebarData$ = this.sidebarData.asObservable();

  protected relativeRoute: ActivatedRoute | null = null;
  settingsExpanded = true;
  sdrfExpanded = true;
  user: any;
  visible = true;

  private _userContextService = inject(UserContextService);
  private permissionContext = inject(PermissionContextService);

  constructor(
    private _authService: AuthService,
    private _router: Router,
    public _route: ActivatedRoute,
  ) {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getCurrentRouteWithSidebar(this._router.routerState.root)),
        filter((route) => !!route.routeConfig?.data?.['sidebar']),
      )
      .subscribe((route) => {
        this.updateRelativeRoute();
        this.updateSidebar(route.routeConfig?.data?.['sidebar'], route.params);
        if (this._router.url.includes('/settings')) {
          this.settingsExpanded = true;
        }
        if (this._router.url.includes('/sdrf')) {
          this.sdrfExpanded = true;
        }
      });
  }

  ngOnInit(): void {
    if (this._router.url.includes('/settings')) this.settingsExpanded = true;
    if (this._router.url.includes('/sdrf')) this.sdrfExpanded = true;
    this.loadUserProfile();
  }

  loadUserProfile() {
    if (this._userContextService.user) this.user = this._userContextService.user;
  }

  private getCurrentRouteWithSidebar(route: ActivatedRoute): ActivatedRoute {
    let result: ActivatedRoute | null = null;
    while (route.firstChild) {
      route = route.firstChild;
      if (route.routeConfig?.data?.['sidebar']) result = route;
    }
    return result ?? route;
  }

  private updateRelativeRoute() {
    let currentRoute = this._route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    this.relativeRoute = currentRoute.parent;
  }

  private hasPermission(required?: string[]): boolean {
    if (!required || required.length === 0) return true;
    return this.permissionContext.hasAny(required);
  }

  private isAllowedForLocationType(item: SidebarItem): boolean {
    if (!item.requiresHeadquarter) return true;
    return this._userContextService.isHeadquarterUser();
  }

  private updateSidebar(sidebarConfig: any, routeParams: Params) {
    const processedData: SidebarData = {
      mainItems: [],
      settingsItems: [],
      sdrfItems: [],
    };

    if (sidebarConfig.items) {
      processedData.mainItems = sidebarConfig.items
        .filter(
          (item: SidebarItem) =>
            this.hasPermission(item.permission) &&
            this.isAllowedForLocationType(item),
        )
        .map((item: SidebarItem) => ({ ...item, link: item.link }));
    }

    if (sidebarConfig.settingsItems) {
      processedData.settingsItems = sidebarConfig.settingsItems
        .filter((item: SidebarItem) => this.hasPermission(item.permission))
        .map((item: SidebarItem) => ({ ...item, link: item.link }));
    }

    if (sidebarConfig.sdrfItems && sidebarConfig.sdrfItems.length > 0) {
      processedData.sdrfItems = sidebarConfig.sdrfItems
        .map((section: any) => {
          const filteredChildren =
            section.children
              ?.filter((item: SidebarItem) => this.hasPermission(item.permission))
              .map((item: SidebarItem) => ({ ...item, link: item.link })) || [];

          return {
            label: section.label,
            icon: section.icon,
            children: filteredChildren,
          };
        })
        .filter((section: SidebarSection) => section.children.length > 0);
    }

    this.sidebarData.next(processedData);
  }

  toggleSettings() {
    this.settingsExpanded = !this.settingsExpanded;
  }

  toggleSdrf() {
    this.sdrfExpanded = !this.sdrfExpanded;
  }

  logout() {
    this._authService.logout();
  }

  openChangePassword(): void {
    this.changePasswordDialog.open();
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map((part) => part.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }

  closeCallback(e: any): void {
    this.drawerRef.close(e);
  }
}